<template>
  <div v-if="visible" class="sncr-banner" :class="'sncr-banner-' + tone">
    <div class="sncr-banner-icon">
      <i class="bi" :class="icon"></i>
    </div>
    <div class="sncr-banner-content">
      <p class="sncr-banner-title">{{ title }}</p>

      <ul v-if="entries.length > 1" class="sncr-banner-list">
        <li v-for="entry in entries" :key="entry.inscription.council_document + entry.regulatory_type">
          <strong>{{ entry.regulatory_type_description }}</strong>
          — {{ remaining(entry) }}
          <span class="sncr-banner-inscription">{{ inscriptionLabel(entry.inscription) }}</span>
        </li>
      </ul>

      <p class="sncr-banner-text">{{ text }}</p>

      <p v-if="visaOnly" class="sncr-banner-text sncr-banner-visa">
        A numeração destes tipos depende de autorização prévia da vigilância sanitária do seu estado.
        Autenticar no gov.br não resolve sozinho.
      </p>
    </div>
    <div class="sncr-banner-actions">
      <button type="button" class="btn btn-sm btn-primary" :disabled="starting" @click="authenticate">
        {{ starting ? 'Abrindo gov.br...' : actionLabel }}
      </button>
      <button v-if="dismissible" type="button" class="btn btn-sm btn-light" @click="later">Depois</button>
    </div>
  </div>
</template>

<script>
import Alert from "../../helpers/alert";
import {dismiss, isDismissed, startGovBrAuth, statusMeta} from "../../helpers/sncr";

export default {
  name: "SncrBalanceBanner",
  props: {
    professionalId: [Number, String],
    returnTo: Object,
  },
  data() {
    return {
      starting: false,
      dismissed: false,
    }
  },
  mounted() {
    this.dismissed = isDismissed(this.professionalId);
  },
  computed: {
    entries() {
      return this.$store.getters['sncr/attention'];
    },
    worst() {
      return this.$store.getters['sncr/worst'];
    },
    visible() {
      if (!this.professionalId || this.entries.length === 0) {
        return false;
      }

      return !(this.dismissible && this.dismissed);
    },
    dismissible() {
      return this.worst === 'LOW';
    },
    tone() {
      return statusMeta(this.worst).tone;
    },
    icon() {
      return statusMeta(this.worst).icon;
    },
    single() {
      return this.entries.length === 1 ? this.entries[0] : null;
    },
    visaOnly() {
      return this.entries.every((entry) => entry.requires_visa_authorization);
    },
    actionLabel() {
      return this.visaOnly ? 'Consultar na Anvisa' : 'Reabastecer agora';
    },
    title() {
      if (!this.single) {
        return this.worst === 'EMPTY'
            ? 'Você está sem numerações para alguns receituários.'
            : 'Suas numerações da Anvisa estão acabando.';
      }

      if (this.single.balance <= 0) {
        return `Você está sem numerações de ${this.single.regulatory_type_description}.`;
      }

      return `Suas numerações de ${this.single.regulatory_type_description} estão acabando.`;
    },
    text() {
      if (this.worst === 'EMPTY') {
        return 'Sem numeração, esse receituário não pode ser emitido. Autenticar no gov.br e obter novos números leva cerca de um minuto.';
      }

      const restam = this.single ? `${this.remaining(this.single)}. ` : '';

      return `${restam}Reabastecer leva menos de um minuto e evita interrupção durante um atendimento.`;
    },
  },
  methods: {
    remaining(entry) {
      if (entry.balance <= 0) {
        return 'nenhum número disponível';
      }

      return entry.balance === 1 ? 'resta 1 número' : `restam ${entry.balance} números`;
    },
    inscriptionLabel(inscription) {
      return `${inscription.council} ${inscription.council_document}/${inscription.council_uf}`;
    },
    later() {
      dismiss(this.professionalId);
      this.dismissed = true;
    },
    authenticate() {
      this.starting = true;

      const destino = this.returnTo ?? {path: this.$route.fullPath, professionalId: this.professionalId};

      startGovBrAuth(this.professionalId, destino).catch(() => {
        this.starting = false;
        Alert.toastError('Não foi possível abrir a autenticação do gov.br. Tente novamente.');
      });
    },
  },
}
</script>

<style scoped>
.sncr-banner {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  border: 1px solid #e9ecef;
  border-left: 4px solid #780f5a;
  border-radius: 10px;
  background: #ffffff;
  padding: 14px 18px;
  margin-bottom: 16px;
}

.sncr-banner-low {
  background: #fff9db;
  border-color: #f1d68a;
  border-left-color: #e0a800;
}

.sncr-banner-critical,
.sncr-banner-empty {
  background: #fff5f5;
  border-color: #f3c0c0;
  border-left-color: #c92a2a;
}

.sncr-banner-icon {
  font-size: 1.35rem;
  line-height: 1;
  padding-top: 2px;
  color: #7a4f00;
}

.sncr-banner-critical .sncr-banner-icon,
.sncr-banner-empty .sncr-banner-icon {
  color: #c92a2a;
}

.sncr-banner-content {
  flex: 1;
}

.sncr-banner-title {
  font-weight: 700;
  color: #212529;
  margin: 0 0 4px 0;
}

.sncr-banner-text {
  color: #495057;
  margin: 0;
}

.sncr-banner-visa {
  margin-top: 6px;
  font-style: italic;
}

.sncr-banner-list {
  margin: 0 0 6px 0;
  padding-left: 18px;
  color: #495057;
}

.sncr-banner-inscription {
  color: #868e96;
  font-size: 0.85rem;
  margin-left: 6px;
}

.sncr-banner-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
</style>
